import { HTMLAttributes, PropsWithChildren, useId } from 'react';
import infoIcon from '@/assets/icons/info2.svg';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import styles from './InfoPanel.module.css';

export interface InfoPanelItem {
  label: string;
  value: string | number;
  hasInfoIcon?: boolean;
  infoTooltip?: string;
}

export interface InfoPanelProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  items: InfoPanelItem[];
  className?: string;
}

const InfoPanel = ({ title, items, className = '', ...props }: InfoPanelProps) => {
  return (
    <div className={`${styles.container} ${className}`.trim()} {...props}>
      <div className={styles.titleSection}>
        <h3 className="body-lr-bold text-(--font-body-5) text-align-start">{title}</h3>
      </div>
      <div className={styles.content}>
        {items.map((item, index) => (
          <InfoPanelCard key={index} item={item} />
        ))}
      </div>
    </div>
  );
};

interface InfoPanelCardProps {
  item: InfoPanelItem;
}

const InfoPanelCard = ({ item }: InfoPanelCardProps) => {
  const tooltipId = useId();
  
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        {item.hasInfoIcon && (
          <>
            <img
              id={tooltipId}
              className={styles.infoIcon}
              src={infoIcon}
              alt="info"
            />
            {item.infoTooltip && (
              <CustomTooltip targetId={tooltipId} placement="top">
                <span className={styles.tooltipText}>{item.infoTooltip}</span>
              </CustomTooltip>
            )}
          </>
        )}
        <span className={styles.label}>{item.label}</span>
      </div>
      <div className={styles.valueContainer}>
        <span className={styles.value}>{item.value}</span>
      </div>
    </div>
  );
};

export interface InfoPanelContainerProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const InfoPanelContainer = ({ 
  className = '', 
  children, 
  ...props 
}: PropsWithChildren<InfoPanelContainerProps>) => {
  return (
    <div className={`${styles.panelContainer} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

export default InfoPanel;

